/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import HeaderFour from "../../components/Header/HeaderFour";
import Footer from "../../components/Footer";
import Seo, { SITE_URL } from "../../components/Seo";
import config from "../../config";
import makeRequest from "../../makeRequest";

const BASE = config.api.baseUrl;

function useBlogImage(blog, field = "image1") {
  const [src, setSrc] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!blog?.id || !blog?.[`has${field.charAt(0).toUpperCase()}${field.slice(1)}`]) return;
    let cancelled = false;
    fetch(`${BASE}/website/blog/${blog.id}/image/${field}`)
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob && !cancelled) {
          const url = URL.createObjectURL(blob);
          ref.current = url;
          setSrc(url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (ref.current) URL.revokeObjectURL(ref.current);
    };
  }, [blog?.id, field]);
  return src;
}

const BlogContent = ({ blog }) => {
  const hero = useBlogImage(blog, "image1");
  const sec = useBlogImage(blog, "image2");
  const date = blog.createdAt ? new Date(blog.createdAt) : null;
  const dateLabel = date ? date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div style={{ background: "#f7fafc", padding: "40px 0 80px" }}>
      <div className="container" style={{ maxWidth: 900 }}>
        <article
          style={{
            background: "#fff",
            borderRadius: 18,
            overflow: "hidden",
            boxShadow: "0 8px 28px rgba(7,108,236,0.07)",
          }}
        >
          {hero && (
            <div style={{ width: "100%", maxHeight: 480, overflow: "hidden" }}>
              <img src={hero} alt={blog.title || blog.name} style={{ width: "100%", height: "auto", display: "block" }} />
            </div>
          )}
          <div style={{ padding: "36px 40px 44px" }}>
            <div style={{ display: "flex", gap: 16, color: "#6b7280", fontSize: "0.85rem", marginBottom: 14 }}>
              {dateLabel && (
                <span>
                  <i className="fa-regular fa-calendar" style={{ marginRight: 6, color: "#076cec" }}></i>
                  {dateLabel}
                </span>
              )}
              <span>
                <i className="fa-regular fa-user" style={{ marginRight: 6, color: "#076cec" }}></i>
                {blog.name || "Maroof"}
              </span>
            </div>
            <h1 style={{ fontSize: "2rem", fontWeight: 800, color: "#0d3b6e", margin: "0 0 18px", lineHeight: 1.25 }}>
              {blog.title || blog.name}
            </h1>
            {blog.description && (
              <div
                style={{ color: "#374151", lineHeight: 1.85, fontSize: "1rem", whiteSpace: "pre-wrap" }}
                dangerouslySetInnerHTML={{ __html: blog.description }}
              />
            )}
            {sec && (
              <div style={{ marginTop: 28 }}>
                <img src={sec} alt="" style={{ width: "100%", borderRadius: 12 }} />
              </div>
            )}

            {(blog.facebookLink || blog.twitterLink || blog.linkedinLink || blog.instagramLink || blog.youtubeLink) && (
              <div style={{ marginTop: 32, paddingTop: 20, borderTop: "1px solid #f1f5f9" }}>
                <strong style={{ color: "#0d3b6e", marginRight: 12 }}>Share:</strong>
                {blog.facebookLink && (
                  <a href={blog.facebookLink} target="_blank" rel="noreferrer" style={shareStyle}>
                    <i className="fa-brands fa-facebook-f"></i>
                  </a>
                )}
                {blog.linkedinLink && (
                  <a href={blog.linkedinLink} target="_blank" rel="noreferrer" style={shareStyle}>
                    <i className="fa-brands fa-linkedin-in"></i>
                  </a>
                )}
                {blog.instagramLink && (
                  <a href={blog.instagramLink} target="_blank" rel="noreferrer" style={shareStyle}>
                    <i className="fa-brands fa-instagram"></i>
                  </a>
                )}
                {blog.youtubeLink && (
                  <a href={blog.youtubeLink} target="_blank" rel="noreferrer" style={shareStyle}>
                    <i className="fa-brands fa-youtube"></i>
                  </a>
                )}
              </div>
            )}

            <div style={{ marginTop: 24 }}>
              <Link
                to="/blog"
                style={{
                  display: "inline-block",
                  padding: "10px 20px",
                  borderRadius: 8,
                  background: "#f1f5f9",
                  color: "#0d3b6e",
                  fontWeight: 700,
                  textDecoration: "none",
                  fontSize: "0.88rem",
                }}
              >
                ← Back to Blog
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

const shareStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 36,
  height: 36,
  borderRadius: "50%",
  background: "#076cec",
  color: "#fff",
  marginRight: 8,
  textDecoration: "none",
};

const BlogDetailsDynamic = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    makeRequest({ route: `/website/blog/${id}`, method: "get" })
      .then(({ data }) => !cancelled && setBlog(data))
      .catch((e) => !cancelled && setError(e?.response?.data?.error || "Blog post not found"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <>
      <Seo
        title={blog?.title || blog?.name || "Blog"}
        description={
          blog?.description
            ? String(blog.description)
                .replace(/<[^>]+>/g, "")
                .slice(0, 160)
            : "Read the latest health insights from Maroof International Hospital."
        }
        path={`/blog/${id}`}
        type="article"
        jsonLd={
          blog && {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: blog.title || blog.name,
            author: { "@type": "Organization", name: blog.name || "Maroof International Hospital" },
            datePublished: blog.createdAt,
            publisher: {
              "@type": "Organization",
              name: "Maroof International Hospital",
              logo: { "@type": "ImageObject", url: `${SITE_URL}/images/logo/logo-full.png` },
            },
            mainEntityOfPage: `${SITE_URL}/blog/${id}`,
          }
        }
      />
      <HeaderFour />
      {loading ? (
        <div style={{ padding: "120px 20px", textAlign: "center", color: "#6b7280" }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 36, color: "#076cec" }}></i>
          <p style={{ marginTop: 16 }}>Loading post…</p>
        </div>
      ) : error || !blog ? (
        <div style={{ padding: "120px 20px", textAlign: "center" }}>
          <h2 style={{ color: "#0d3b6e" }}>Post not found</h2>
          <p style={{ color: "#6b7280" }}>{error}</p>
          <Link to="/blog" style={{ color: "#076cec", fontWeight: 700 }}>
            ← Back to all posts
          </Link>
        </div>
      ) : (
        <BlogContent blog={blog} />
      )}
      <Footer />
    </>
  );
};

export default BlogDetailsDynamic;
